import type { Species } from "@/types";
import { BackIcon } from "@/app/components/system/Icons";

/** Identity fields needed to render a candidate's avatar and names. */
type CandidateSpeciesCardSpecies =
  & Pick<Species, "scientific" | "color" | "initial" | "labelImageUrl">
  & { common: string | null };

/**
 * A tappable card for one photo-recognition candidate. Unlike KnownSpeciesCard
 * (a tight single-row card), this shows a wrapped reasoning sentence beneath the
 * names, so the user can weigh why each species was proposed before picking.
 */
export function CandidateSpeciesCard({
  species: s,
  reasoning,
  onClick,
}: Readonly<{
  species: CandidateSpeciesCardSpecies;
  reasoning: string;
  onClick: () => void;
}>) {
  const title = s.common || s.scientific;
  const subtitle = s.common && s.scientific && s.common !== s.scientific ? s.scientific : "";
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-start gap-3.5 p-4.5 bg-paper border border-line rounded-[18px] cursor-pointer text-left transition-all duration-150 text-ink hover:border-moss-2 hover:-translate-y-px active:scale-[0.99]"
    >
      <span
        className="w-13 h-13 rounded-2xl overflow-hidden grid place-items-center shrink-0 text-paper [&>img]:w-full [&>img]:h-full [&>img]:block [&>img]:object-cover"
        style={{ background: s.color }}
      >
        {s.labelImageUrl
          ? <img src={s.labelImageUrl} alt="" />
          : <span className="font-display font-medium text-[22px]">{s.initial || "?"}</span>}
      </span>
      <span className="flex-1 flex flex-col gap-0.5 min-w-0">
        <strong className="text-base font-medium">{title}</strong>
        {subtitle && (
          <em className="italic text-[12.5px] text-ink-3 opacity-75 leading-[1.4]">{subtitle}</em>
        )}
        {reasoning && (
          <span className="text-[13px] text-ink-2 leading-[1.45] mt-1">{reasoning}</span>
        )}
      </span>
      <BackIcon size={18} className="shrink-0 mt-0.5" />
    </button>
  );
}
